/**
 * Sysadmin Fine-Tuning → Golden Set (Phase 5a infrastructure).
 *
 * CRUD over per-tenant `golden_qa_pairs_ft` rows. Pairs are scored by
 * the evaluation orchestrator (Phase 5b plugs in real scoring) when
 * a candidate adapter starts a run.
 *
 * Classification + export_control axes drive ITAR/CMMC handling
 * per CLAUDE.md §11.3.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ListChecks,
  Plus,
  RefreshCw,
  Trash2,
  Shield,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatTile } from '@/components/ui/stat-tile';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useFtGoldenSet,
  useCreateFtGoldenPair,
  useDeleteFtGoldenPair,
  type FtGoldenPairRow,
  type FtClassification,
  type FtExportControl,
  type FtDifficulty,
} from '@/lib/use-sysadmin-fine-tuning';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/fine-tuning/golden-set')({
  component: FtGoldenSetPage,
});

function FtGoldenSetPage() {
  const { data, isLoading, error, refetch, isFetching } = useFtGoldenSet();
  const [creating, setCreating] = useState(false);

  const items = data?.items ?? [];

  const counts = {
    total: items.length,
    active: items.filter((r) => r.isActive).length,
    itar: items.filter((r) => r.exportControl === 'itar').length,
    cui: items.filter((r) => r.classification === 'cui').length,
  };

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load golden set"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ListChecks className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Golden Set
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">
                Phase 5a
              </Badge>
              <span className="text-xs text-muted-foreground">
                Per-tenant Q&A pairs that score candidate adapters.
                Classification axes drive ITAR / CMMC inheritance.
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New pair
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={ListChecks} tone="default" />
        <StatTile label="Active" value={String(counts.active)} icon={ListChecks} tone={counts.active > 0 ? 'success' : 'muted'} />
        <StatTile label="ITAR" value={String(counts.itar)} icon={Shield} tone={counts.itar > 0 ? 'warning' : 'muted'} />
        <StatTile label="CUI" value={String(counts.cui)} icon={Shield} tone={counts.cui > 0 ? 'default' : 'muted'} />
      </div>

      {isLoading && items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading golden set…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No golden pairs yet"
          body="Create at least one Q&A pair per tenant before running an evaluation. Classification + export_control drive how the pair's score propagates into the audit chain."
        />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Tenant</th>
                <th className="px-3 py-2 font-medium">Question</th>
                <th className="px-3 py-2 font-medium">Classification</th>
                <th className="px-3 py-2 font-medium">Difficulty</th>
                <th className="px-3 py-2 font-medium text-right">Last score</th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <GoldenRow key={r.id} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateGoldenDialog
          open={creating}
          onOpenChange={setCreating}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}

function GoldenRow({ row }: { row: FtGoldenPairRow }) {
  const del = useDeleteFtGoldenPair();
  return (
    <tr className="border-t hover:bg-muted/30 transition-colors">
      <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
        {row.tenantId.slice(0, 8)}
      </td>
      <td className="px-3 py-2.5 max-w-md truncate text-xs" title={row.question}>
        {row.question.slice(0, 140)}
      </td>
      <td className="px-3 py-2.5 text-xs">
        <div className="flex items-center gap-1 flex-wrap">
          <Badge
            variant="outline"
            className={cn(
              'text-[10px]',
              row.classification !== 'unclassified' &&
                'border-amber-500/40 text-amber-700 dark:text-amber-300'
            )}
          >
            {row.classification}
          </Badge>
          {row.exportControl !== 'none' && (
            <Badge
              variant="outline"
              className="text-[10px] border-red-500/40 text-red-700 dark:text-red-300"
            >
              {row.exportControl.toUpperCase()}
            </Badge>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs">
        {row.difficulty ?? <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">
        {row.lastEvaluatedScore != null
          ? row.lastEvaluatedScore.toFixed(3)
          : '—'}
      </td>
      <td className="px-3 py-2.5">
        {row.isActive ? (
          <Badge variant="secondary" className="text-[10px]">
            Active
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">
            Inactive
          </Badge>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => del.mutate({ id: row.id })}
          disabled={!row.isActive || del.isPending}
          title={row.isActive ? 'Soft-deactivate' : 'Already inactive'}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function CreateGoldenDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [question, setQuestion] = useState('');
  const [expectedAnswer, setExpectedAnswer] = useState('');
  const [classification, setClassification] =
    useState<FtClassification>('unclassified');
  const [exportControl, setExportControl] =
    useState<FtExportControl>('none');
  const [difficulty, setDifficulty] = useState<FtDifficulty | ''>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const create = useCreateFtGoldenPair();

  const tenantValid = /^[0-9a-f-]{36}$/.test(tenantId);
  const canSubmit =
    tenantValid &&
    question.trim().length > 0 &&
    expectedAnswer.trim().length > 0 &&
    !create.isPending;

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await create.mutateAsync({
        tenantId,
        question: question.trim(),
        expectedAnswer: expectedAnswer.trim(),
        classification,
        exportControl,
        difficulty: difficulty === '' ? null : difficulty,
        tags: [],
      });
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New golden pair</DialogTitle>
          <DialogDescription>
            Adds a Q&A pair to a tenant's golden set. Classification + export_control inherit into the audit chain at evaluation time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Tenant ID <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="00000000-0000-0000-0000-000000000000"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="font-mono text-xs"
            />
            {tenantId.length > 0 && !tenantValid && (
              <p className="text-[10px] text-destructive">Must be a valid UUID.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Question <span className="text-red-500">*</span>
            </Label>
            <Textarea
              placeholder="What is the maintenance frequency for pump P-401?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Expected answer <span className="text-red-500">*</span>
            </Label>
            <Textarea
              placeholder="Per SOP-MAINT-014, P-401 requires…"
              value={expectedAnswer}
              onChange={(e) => setExpectedAnswer(e.target.value)}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Classification</Label>
              <Select
                value={classification}
                onValueChange={(v) => setClassification(v as FtClassification)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unclassified">Unclassified</SelectItem>
                  <SelectItem value="cui">CUI</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Export control</Label>
              <Select
                value={exportControl}
                onValueChange={(v) => setExportControl(v as FtExportControl)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="itar">ITAR</SelectItem>
                  <SelectItem value="ear">EAR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Difficulty</Label>
            <Select
              value={difficulty}
              onValueChange={(v) => setDifficulty(v as FtDifficulty | '')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Optional" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending && (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
